import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

interface ImagePreviewProps {
  previewImage: string | null;
  onClose: (e: React.MouseEvent) => void;
  onImageClick: (e: React.MouseEvent) => void;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({
  previewImage,
  onClose,
  onImageClick
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (previewImage) {
      setIsLoading(true);
      setError(null);
      
      const img = new Image();
      img.src = previewImage;
      
      img.onload = () => {
        setIsLoading(false);
      };
      
      img.onerror = () => {
        setError('Failed to load image preview');
        setIsLoading(false);
      };
    }
  }, [previewImage]);

  useEffect(() => {
    if (!previewImage) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose(e as unknown as React.MouseEvent);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [previewImage, onClose]);

  useEffect(() => {
    if (previewImage && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [previewImage]);

  if (!previewImage) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
    >
      <div className="relative max-w-4xl max-h-[90vh] overflow-auto" onClick={onImageClick}>
        <button
          ref={closeButtonRef}
          onClick={onClose}
          className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-colors"
          aria-label="Close preview"
        >
          <X size={24} aria-hidden="true" />
        </button>
        {isLoading && (
          <div className="flex items-center justify-center bg-gray-900 rounded-lg p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        <img 
          src={previewImage} 
          alt="Preview" 
          className={`max-w-full max-h-[90vh] object-contain transition-opacity duration-200 ${
            isLoading ? 'opacity-0' : 'opacity-100'
          }`}
        />
      </div>
    </div>
  );
};

export default ImagePreview;